import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import { useIntegrationStatus, useRevokeIntegrationClient } from '../../hooks/useWiberryAgent';
import IntegrationStatusIndicators, { IntegrationSyncErrorLine } from './IntegrationStatusIndicators';
import WiberryRunsList from './WiberryRunsList';
import { formatDateTime } from './wiberryFormat';
import './WiberryAgentPanel.css';

/**
 * The "Integrationen" page's wiberry section, rebuilt on the v2 surfaces (S10).
 *
 * **What changed and why.** This panel used to drive v1's bespoke enrolment: a
 * single-use token minted by `POST /integrations/agent/enrolment-tokens`, shown
 * once in a dialog, pasted into `hofsync-agent enroll --token …`. Every one of
 * those routes is gone, and so is the client that spoke them — A2 deleted the
 * agent's v1 protocol half in v34.1.0, so from that release the token this panel
 * minted could not be redeemed by any shipped agent. It was a button that
 * produced a dead string.
 *
 * Pairing is now RFC 8628 device flow: the agent prints a code, the farmer opens
 * `/connect`, and consent binds the client to their session. So this panel no
 * longer *starts* anything — it reports what is connected, whether it is alive,
 * what the last sync moved, what the agent said went wrong, and offers the one
 * destructive action (disconnect).
 *
 * The connected-agent identity comes from `GET /integrations/status`, which
 * reads `oauth_clients`. v1's `GET /integrations/agent/agents` read
 * `integration_agents` — a table the device flow never writes, which is why the
 * old panel showed nothing for a correctly paired agent.
 */
const WiberryAgentPanel: React.FC = () => {
  // Generated, not hardcoded — same reason as IntegrationAgentImports (#3480).
  const headingId = `wiberry-panel-title-${React.useId()}`;
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);

  const { data: status, isLoading, isError } = useIntegrationStatus();
  const connected = Boolean(status?.connected);
  const revoke = useRevokeIntegrationClient();

  const handleRevoke = useCallback(async () => {
    if (!status?.client_id) return;
    try {
      await revoke.mutateAsync(status.client_id);
      setConfirmingRevoke(false);
      showToast(t('integrations.wiberry.revoked', 'Agent getrennt.'), 'success');
    } catch {
      showToast(t('integrations.wiberry.revokeFailed', 'Trennen fehlgeschlagen.'), 'error');
    }
  }, [status?.client_id, revoke, showToast, t]);

  return (
    <section className="wiberry-panel" aria-labelledby={headingId}>
      <div className="wiberry-panel__header">
        <h2 id={headingId} className="wiberry-panel__title">
          {t('integrations.wiberry.title', 'Sync-Agent')}
        </h2>
        <p className="wiberry-panel__description">
          {t(
            'integrations.wiberry.description',
            'Verbinde den wiberry-Sync-Agenten, um Erntedaten automatisch in den digitalen Zwilling zu übernehmen.'
          )}
        </p>
      </div>

      {isLoading && (
        <div
          className="wiberry-panel__spinner"
          role="status"
          aria-label={t('integrations.wiberry.loading', 'Wird geladen…')}
        />
      )}

      {!isLoading && isError && (
        <p className="wiberry-panel__error" role="alert">
          {t('integrations.wiberry.error', 'wiberry-Agent-Status konnte nicht geladen werden.')}
        </p>
      )}

      {!isLoading && !isError && status && (
        <>
          <IntegrationStatusIndicators status={status} />
          <IntegrationSyncErrorLine status={status} />

          {connected && (
            <div className="wiberry-panel__agent">
              <dl className="wiberry-panel__agent-facts">
                <div>
                  <dt>{t('integrations.wiberry.agentName', 'Agent')}</dt>
                  <dd data-testid="wiberry-agent-name">
                    {status.name || t('integrations.wiberry.unnamedAgent', 'Unbenannter Agent')}
                  </dd>
                </div>
                <div>
                  <dt>{t('integrations.wiberry.pairedAt', 'Verbunden seit')}</dt>
                  <dd>{formatDateTime(status.paired_at)}</dd>
                </div>
              </dl>

              {!confirmingRevoke && (
                <button
                  type="button"
                  className="wiberry-panel__revoke"
                  onClick={() => setConfirmingRevoke(true)}
                  data-testid="wiberry-revoke"
                >
                  {t('integrations.wiberry.revoke', 'Agent trennen')}
                </button>
              )}

              {confirmingRevoke && (
                <div className="wiberry-panel__confirm" role="alertdialog" aria-label="Agent trennen">
                  <p>
                    {t(
                      'integrations.wiberry.revokeConfirm',
                      'Der Agent überträgt danach keine Daten mehr. Bereits übernommene Daten bleiben erhalten.'
                    )}
                  </p>
                  <div className="wiberry-panel__confirm-actions">
                    <button
                      type="button"
                      className="wiberry-panel__revoke"
                      onClick={handleRevoke}
                      disabled={revoke.isPending}
                      data-testid="wiberry-revoke-confirm"
                    >
                      {t('integrations.wiberry.revokeConfirmYes', 'Ja, trennen')}
                    </button>
                    <button type="button" onClick={() => setConfirmingRevoke(false)} disabled={revoke.isPending}>
                      {t('common.cancel', 'Abbrechen')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {connected && <WiberryRunsList hasEverSynced={Boolean(status.last_sync)} />}
        </>
      )}
    </section>
  );
};

export default WiberryAgentPanel;
